'use client';
import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';

NProgress.configure({
  showSpinner: false,
  trickleSpeed: 150,
  minimum: 0.08,
  speed: 300,
});

const style = `
  #nprogress .bar {
    background: #bf0000 !important;
    height: 6px !important;
    border-radius: 3px !important;
    top: 0 !important;
    z-index: 9999 !important;
    box-shadow: none !important;
  }
  #nprogress .peg {
    box-shadow: none !important;
    background: #bf0000 !important;
  }
  #nprogress .spinner,
  #nprogress .spinner-icon {
    display: none !important;
  }
`;

const CHECK_INTERVAL = 150;
const STABLE_TICKS_NEEDED = 3;
const MAX_DURATION = 8000;

function ProgressBar() {
  const pathname = usePathname();
  const pendingFetchesRef = useRef(0);

  useEffect(() => {
    const el = document.createElement('style');
    el.textContent = style;
    document.head.appendChild(el);
    return () => el.remove();
  }, []);

  useEffect(() => {
    const originalFetch = window.fetch;

    window.fetch = (...args) => {
      pendingFetchesRef.current++;
      return originalFetch.apply(window, args).finally(() => {
        pendingFetchesRef.current = Math.max(0, pendingFetchesRef.current - 1);
      });
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  useEffect(() => {
    let finished = false;
    let stableTicks = 0;

    NProgress.start();

    const finish = () => {
      if (finished) return;
      finished = true;
      clearInterval(intervalId);
      clearTimeout(maxTimeoutId);
      NProgress.done();
    };

    const hasPendingVisibleImages = () => {
      const images = document.images;
      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        if (img.complete) continue;

        const rect = img.getBoundingClientRect();
        const inViewport =
          rect.bottom > 0 &&
          rect.top < window.innerHeight &&
          rect.right > 0 &&
          rect.left < window.innerWidth;

        if (inViewport) return true;
      }
      return false;
    };

    const intervalId = setInterval(() => {
      const busy =
        pendingFetchesRef.current > 0 ||
        document.readyState !== 'complete' ||
        hasPendingVisibleImages();

      if (busy) {
        stableTicks = 0;
        return;
      }

      stableTicks++;
      if (stableTicks >= STABLE_TICKS_NEEDED) finish();
    }, CHECK_INTERVAL);

    const maxTimeoutId = setTimeout(finish, MAX_DURATION);

    return () => {
      finished = true;
      clearInterval(intervalId);
      clearTimeout(maxTimeoutId);
    };
  }, [pathname]);

  return null;
}

export default ProgressBar;